import React, { useState, useRef, useEffect } from 'react';

interface SearchableIngredientDropdownProps {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
}

export const SearchableIngredientDropdown: React.FC<SearchableIngredientDropdownProps> = ({
  value,
  options,
  onChange,
  disabled = false,
  placeholder = 'Select...',
  style
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Reset search term when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setHighlightedIndex(0);
    }
  }, [isOpen]);

  // Reset highlighted index when search term changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchTerm]);

  const handleSelect = (option: string) => {
    onChange(option);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions.length > 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
    }
  };

  const getDisplayValue = () => {
    if (value) return value;
    if (disabled && !options.length) return '(Select supplier first)';
    if (disabled) return placeholder;
    if (!options.length) return '(No options available)';
    return placeholder;
  };

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    ...style
  };

  const buttonStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: disabled ? '#f5f5f5' : 'white',
    cursor: disabled ? 'not-allowed' : 'pointer',
    textAlign: 'left',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: value ? '#333' : '#999'
  };

  const dropdownStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    backgroundColor: 'white',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    zIndex: 1000,
    maxHeight: '250px',
    display: 'flex',
    flexDirection: 'column'
  };

  const searchInputStyle: React.CSSProperties = {
    padding: '8px',
    border: 'none',
    borderBottom: '1px solid #ddd',
    fontSize: '14px',
    outline: 'none',
    width: '100%'
  };

  const optionsListStyle: React.CSSProperties = {
    overflowY: 'auto',
    maxHeight: '200px'
  };

  const optionStyle = (index: number, isHovered: boolean): React.CSSProperties => ({
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '14px',
    backgroundColor: index === highlightedIndex || isHovered ? '#e3f2fd' : 'transparent',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  });

  const emptyMessageStyle: React.CSSProperties = {
    padding: '16px',
    textAlign: 'center',
    color: '#666',
    fontSize: '13px'
  };

  return (
    <div ref={dropdownRef} style={containerStyle} onKeyDown={handleKeyDown}>
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={buttonStyle}
        tabIndex={disabled ? -1 : 0}
      >
        <span>{getDisplayValue()}</span>
        <span
          style={{
            fontSize: '12px',
            color: '#666',
            transition: 'transform 0.2s',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            marginLeft: '8px'
          }}
        >
          ▼
        </span>
      </div>

      {isOpen && !disabled && (
        <div style={dropdownStyle}>
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type to filter..."
            style={searchInputStyle}
          />

          <div style={optionsListStyle}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={option}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  style={optionStyle(index, false)}
                >
                  {option}
                </div>
              ))
            ) : (
              <div style={emptyMessageStyle}>
                {options.length === 0 ? '(No options available)' : 'No matches found'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
